import { trpc } from "@/util/trpc";
import { useCallback } from "react";
import type { Hex } from "viem";
import { usePublicClient } from "wagmi";

/**
 * This indexes the blockchain and returns as soon as the transaction is included in the index.
 * @param txHash The transaction to check for
 */
export function useWaitForTransactionToBeIndexed() {
    const client = usePublicClient();
    const blockchainIndex = trpc.lambda.utils.blockchainIndex.useMutation();
    const trpcUtils = trpc.useUtils();

    const waitForTransactionToBeIndexed = useCallback(
        async (txHash: string) => {
            try {
                console.log("Waiting for transaction to be included...");
                await client?.waitForTransactionReceipt({
                    hash: txHash as Hex,
                    timeout: 300_000,
                });

                await blockchainIndex.mutateAsync({ txHash });

                for (let i = 0; i < 15; i++) {
                    console.log("Waiting for transaction to be indexed... ", i + 1);
                    const exists = await trpcUtils.lambda.utils.doesTransactionExist.fetch({
                        transactionHash: txHash,
                    });

                    if (exists) {
                        console.log("Transaction was indexed!");
                        return;
                    }

                    // Wait 5 seconds between calls
                    const waitMilliseconds = 5000;
                    await new Promise((resolve) => setTimeout(resolve, waitMilliseconds));
                }

                throw new Error(`Indexing failed for transaction. Timeout: ${txHash}`);
            } catch (error) {
                console.error(error);
                throw new Error(`Indexing failed for transaction: ${txHash}`);
            }
        },
        [blockchainIndex, client, trpcUtils],
    );

    return { waitForTransactionToBeIndexed };
}
